import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, useOutsideClickEffect } from '@saviynt/common';
import {
  Badge,
  Button,
  ButtonSelect,
  Icon,
  Link,
  Loader,
  MobileDrawer,
  RadioButton,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  appliedSaviSmartFilter,
  clearAllFiltersAction,
  clearSmartFiltersAction,
  fetchSmartFiltersMeta,
  selectAllBulkAction,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setCheckForBulkAction,
  setIndeterminateForBulkAction,
  setIsSmartApplied,
  setSmartFilterId,
  updateCertSmartFilter,
} from '../../../../redux/certdetails/action';
import { ITEMS_API_OFFSET, VIEW_OPTIONS } from '../../../../utils/constant';
import { useInvertedLocalize } from '../../../../utils/localization';
import { getMessageKey } from '../../../../utils/utils';
import { invertedMessages } from '../messages';

import './SmartFilters.css';

function LoaderWrapper() {
  return (
    <div className='SmartFilters-loader'>
      <Loader kind='dots' format='inline' size='medium' color='brand' />
    </div>
  );
}

// Commented the below lines because title is removed from design for sometime
function FactorsTitle() {
  return (
    <div className='SmartFilters-factorsTitleWrapper'>
      {/* <Icon kind={iconKind} size='medium' />
      <div>
        <Typography kind='h3' className='SmartFilters-title'>
          {title}
        </Typography>
        <Typography kind='body3' className='SmartFilters-description'>
          {description}
        </Typography>
      </div> */}
    </div>
  );
}

function FactorItem({
  id,
  title,
  description,
  payload,
  handleChange,
  isDisabled,
  selectedFactor,
}) {
  const [isChecked, setIschecked] = useState(id === selectedFactor);

  useEffect(() => {
    setIschecked(id === selectedFactor);
  }, [selectedFactor]);

  const handleItemClick = (event) => {
    const _isChecked = !isChecked;

    setIschecked(_isChecked);
    handleChange(id, {
      ...payload,
      checked: _isChecked,
    });
    event.preventDefault();
  };

  return (
    <RadioButton
      id={`smartFiltersRadioButton${Math.random()}`}
      key={`smartFiltersRadioButton${Math.random()}`}
      name='smartFiltersRadioButtonGroup'
      title={title}
      description={description}
      value={id}
      isDisabled={isDisabled}
      radioSelection={selectedFactor}
      onClick={handleItemClick}
      titleLineLimit={2}
      descriptionLineLimit={2}
    />
  );
}

FactorItem.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  payload: PropTypes.shape({}).isRequired,
  handleChange: PropTypes.func.isRequired,
  selectedFactor: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
};

FactorItem.defaultProps = {
  isDisabled: false,
};

function SmartFilters({
  setShowBadgeOnFilterButton,
  clearDropDownFilters,
  setIsSmartFilterApplied,
  isMobileDrawerOpen,
  setIsMobileDrawerOpen,
  isMobileResponsivenessEnabled,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const l = useInvertedLocalize(invertedMessages);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedFactor, setSelectedFactor] = useState('');
  const [shouldShowFilter, setShouldShowFilter] = useState(true);

  const { messages } = useSelector((state) => state?.user);
  const {
    core: { isLoading },
    global: { certKey },
    viewControls: {
      smartFiltersMeta,
      smartFilterId,
      clearSmartFilters,
      clearAllFilters,
    },
    personalization: { viewOption },
    bulkAction,
  } = useSelector((state) => state?.certifications?.details);

  const smartFilterWrapperRef = useRef(null);
  const smartFilterDropdownRef = useRef(null);

  const personalizationViewOption = viewOption?.view;

  const calculateShouldShowSmartFilter = () => {
    if (!smartFiltersMeta || Object.keys(smartFiltersMeta).length === 0) {
      return false;
    }

    const hasFactors =
      smartFiltersMeta.risk_factors &&
      smartFiltersMeta.risk_factors.factors &&
      smartFiltersMeta.risk_factors.factors.length > 0;

    return hasFactors;
  };

  useOutsideClickEffect(smartFilterWrapperRef, () => setIsOpen(false));

  useEffect(() => {
    if (certKey && Object.keys(smartFiltersMeta)?.length === 0) {
      dispatch(fetchSmartFiltersMeta(certKey));
    }
  }, [certKey]);

  useEffect(() => {
    setSelectedFactor(smartFilterId);
  }, [smartFilterId]);

  useEffect(() => {
    setShouldShowFilter(calculateShouldShowSmartFilter());
  }, [smartFiltersMeta]);

  const clearLocalFilters = () => {
    setIsSmartFilterApplied(false);
    dispatch(setSmartFilterId(''));
    dispatch(setIsSmartApplied(false));
    setShowBadgeOnFilterButton(false);
  };

  const clearFilter = () => {
    if (isMobileResponsivenessEnabled) {
      setIsMobileDrawerOpen(false);
    }

    clearDropDownFilters();

    // don't call certssmartfilter to clear when clearing the other filters
    if (!clearAllFilters) {
      dispatch(
        updateCertSmartFilter(
          {
            certkey: certKey,
            clearFilter: 'true',
          },
          {
            ITEMS_API_OFFSET,
          }
        )
      );
    }

    clearLocalFilters();
  };

  useEffect(() => {
    if (clearAllFilters) {
      clearFilter();
      dispatch(clearAllFiltersAction(false));
    }
  }, [clearAllFilters]);

  useEffect(() => {
    if (clearSmartFilters) {
      clearLocalFilters();
      dispatch(clearSmartFiltersAction(false));
    }
  }, [clearSmartFilters]);

  const updateSmartFilter = (id, payload) => {
    clearDropDownFilters();

    if (bulkAction?.isChecked || bulkAction?.isIndeterminate) {
      dispatch(setBulkActionBySelectAll(true));

      dispatch(selectAllBulkAction(certKey, true))
        .then(() => {
          dispatch(setCheckForBulkAction(false));
          dispatch(setIndeterminateForBulkAction(false));
        })
        .catch((error) => {
          dispatch(setBulkActionAlert(true, error.message));
        });
    }

    if (payload.checked) {
      dispatch(
        updateCertSmartFilter(
          {
            ...payload,
            checked: payload.checked ? '1' : '0',
          },
          {
            ITEMS_API_OFFSET,
            smartFilterIdToSet: id,
          }
        )
      );
    } else {
      dispatch(
        updateCertSmartFilter(
          {
            certkey: certKey,
            clearFilter: 'true',
          },
          {
            ITEMS_API_OFFSET,
          }
        )
      );
    }

    setIsSmartFilterApplied(payload.checked);
    setShowBadgeOnFilterButton(payload.checked);
  };

  useEffect(() => {
    // This function is used to get applied savi smart filter for exporting
    const findAppliedSmartFilterForExport = () => {
      if (!smartFiltersMeta || Object.keys(smartFiltersMeta).length === 0)
        return;

      // Get the first entry of smartFiltersMeta and its factors
      const metaData = Object.entries(smartFiltersMeta)[0];
      const smartFiltersMetaFactors = metaData[1]?.factors;

      // Find the selected factor in smart filters
      const appliedSmartFilter = smartFiltersMetaFactors?.find(
        (filter) => filter.id === selectedFactor
      );

      if (!appliedSmartFilter) {
        // clear smart filter text - using export
        dispatch(appliedSaviSmartFilter(''));

        return;
      }

      // Get the title of the applied filter
      const appliedSmartFilterTitle = appliedSmartFilter?.title;
      const appliedSmartFilterTitleLabel =
        messages[appliedSmartFilterTitle] ||
        localize(getMessageKey(appliedSmartFilterTitle));

      dispatch(appliedSaviSmartFilter(appliedSmartFilterTitleLabel));
    };

    findAppliedSmartFilterForExport();
  }, [selectedFactor]);

  useEffect(() => {
    if (!smartFilterDropdownRef.current) return;

    const updateFocusableElements = () => {
      const focusableElements = smartFilterDropdownRef.current
        .querySelectorAll(`.SmartFilter-dropdown button, 
         .SmartFilter-dropdown [href], 
         .SmartFilter-dropdown input, 
         .SmartFilter-dropdown select, 
         .SmartFilter-dropdown textarea, 
         .SmartFilter-dropdown [tabindex]:not([tabindex="-1"])`);

      focusableElements.forEach((el) => {
        if (!isOpen) {
          // Store previous tabindex if not already stored
          if (!el.hasAttribute('data-prev-tabindex')) {
            el.setAttribute(
              'data-prev-tabindex',
              el.getAttribute('tabindex') || '0'
            );
          }

          // eslint-disable-next-line no-param-reassign
          el.tabIndex = -1;
        } else {
          const prevTabIndex = el.getAttribute('data-prev-tabindex');

          if (prevTabIndex !== null) {
            // eslint-disable-next-line no-param-reassign
            el.tabIndex = parseInt(prevTabIndex, 10);
            el.removeAttribute('data-prev-tabindex');
          } else {
            el.removeAttribute('tabindex');
          }
        }
      });
    };

    updateFocusableElements();

    // MutationObserver to detect when elements become visible
    const observer = new MutationObserver(() => {
      updateFocusableElements();
    });

    observer.observe(smartFilterDropdownRef.current, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [isOpen, smartFilterDropdownRef]);

  useEffect(() => {
    if (
      personalizationViewOption &&
      personalizationViewOption !== VIEW_OPTIONS.MY_PERSONALIZED_VIEW
    ) {
      if (isMobileResponsivenessEnabled) {
        setIsMobileDrawerOpen(false);
      }

      clearDropDownFilters();
      clearLocalFilters();
    }
  }, [personalizationViewOption]);

  if (!shouldShowFilter) {
    return null;
  }

  if (isMobileResponsivenessEnabled) {
    return (
      <>
        <div className='SmartFilter-dropdown-displayFlex--mobileResponsive'>
          {Object.entries(smartFiltersMeta).map(
            ([
              factorType,
              {
                title: factorTitle,
                description: factorDescription,
                iconKind: factorIconKind,
                factors,
              },
            ]) => (
              <div key={factorType} className='SmartFilter-factorsPartition'>
                <FactorsTitle
                  title={
                    messages[factorTitle] ||
                    localize(getMessageKey(factorTitle))
                  }
                  description={
                    messages[factorDescription] ||
                    localize(getMessageKey(factorDescription))
                  }
                  iconKind={factorIconKind}
                />
                <div className='SmartFilter-factorsItemList--mobileResponsive'>
                  {factors?.map(
                    ({ id, title, description, iconKind, payload }) => (
                      <FactorItem
                        key={id}
                        id={id}
                        title={
                          messages[title] || localize(getMessageKey(title))
                        }
                        description={
                          messages[description] ||
                          localize(getMessageKey(description))
                        }
                        isDisabled={isLoading}
                        iconKind={iconKind}
                        payload={payload}
                        handleChange={updateSmartFilter}
                        selectedFactor={selectedFactor}
                      />
                    )
                  )}
                </div>
              </div>
            )
          )}
        </div>
        <MobileDrawer
          isOpen={isMobileDrawerOpen}
          setIsOpen={setIsMobileDrawerOpen}
          isSwipeEnabled
          content={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={clearFilter}
              isDisabled={!selectedFactor}
              className='AdvancedFilters-filterDrawerClearAndRemoveBtns'>
              Clear Filter
            </Button>
          }
        />
      </>
    );
  }

  // if updating `SmartFilter-wrapper` class, `constant.js` on Line -> 25

  return (
    <div className='SmartFilter-wrapper' ref={smartFilterWrapperRef}>
      <div className='SmartFilterButtonSelect-wrapper'>
        <ButtonSelect
          kind='pill'
          size='medium'
          className={`SmartFilter-button ${
            isOpen ? 'SmartFilter-button SmartFilter-button--IsOpen' : ''
          }`}
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          label={
            messages.CERTS_SMART_FILTERS ||
            localize(intl, getMessageKey('CERTS_SMART_FILTERS'))
          }
          prefixIcon={<Icon kind='SmartBrain' size='medium' />}
          BadgeComp={
            selectedFactor ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : null
          }
          isIconSvgFilled={false}
        />
      </div>
      <div
        ref={smartFilterDropdownRef}
        className={classnames(
          'SmartFilter-dropdown',
          isOpen && 'SmartFilter-dropdown--isVisible'
        )}>
        {isLoading ? <LoaderWrapper /> : null}
        <div className='SmartFilter-dropdown-displayFlex'>
          {Object.entries(smartFiltersMeta).map(
            ([
              factorType,
              {
                title: factorTitle,
                description: factorDescription,
                iconKind: factorIconKind,
                factors,
              },
            ]) => (
              <div key={factorType} className='SmartFilter-factorsPartition'>
                <FactorsTitle
                  title={
                    messages[factorTitle] ||
                    localize(intl, getMessageKey(factorTitle))
                  }
                  description={
                    messages[factorDescription] ||
                    localize(intl, getMessageKey(factorDescription))
                  }
                  iconKind={factorIconKind}
                />
                <div className='SmartFilter-factorsItemList'>
                  {factors?.map(
                    ({ id, title, description, iconKind, payload }) => (
                      <FactorItem
                        key={id}
                        id={id}
                        title={
                          messages[title] ||
                          localize(intl, getMessageKey(title))
                        }
                        description={
                          messages[description] ||
                          localize(intl, getMessageKey(description))
                        }
                        iconKind={iconKind}
                        payload={payload}
                        handleChange={updateSmartFilter}
                        selectedFactor={selectedFactor}
                      />
                    )
                  )}
                </div>
              </div>
            )
          )}
        </div>
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          className='SmartFilter-footer'
          role='button'
          onKeyDown={(e) => {
            if (e.key === 'Enter' && selectedFactor) {
              clearFilter();
            }
          }}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            text={l('Clear')}
            kind='button'
            onClick={clearFilter}
            isDisabled={!selectedFactor}
            className='MenuMultiFooter-selectAll'
            size='small'
          />
        </div>
      </div>
    </div>
  );
}

SmartFilters.propTypes = {
  setShowBadgeOnFilterButton: PropTypes.func.isRequired,
  clearDropDownFilters: PropTypes.func.isRequired,
  setIsSmartFilterApplied: PropTypes.func.isRequired,
  isMobileDrawerOpen: PropTypes.bool,
  setIsMobileDrawerOpen: PropTypes.func,
  isMobileResponsivenessEnabled: PropTypes.bool,
};

SmartFilters.defaultProps = {
  isMobileDrawerOpen: false,
  setIsMobileDrawerOpen: () => {},
  isMobileResponsivenessEnabled: false,
};

export default SmartFilters;
